/**
 * Página WL > Configurações
 *
 * Shell com abas internas que consolidam as configurações do painel WL:
 *  - Geral: branding e preferências.
 *  - Suporte: widget de suporte interno + acesso remoto (WebRTC).
 *  - Tenant interno: configuração definitiva do tenant interno do WL.
 *  - Integração CRM: criação automática de negócios para novos tenants.
 *
 * A aba ativa é controlada por `?tab=` para suportar deep-links.
 */
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import WlBrandingSettingsCard from '@/components/wl/settings/WlBrandingSettingsCard';
import WlSupportSettingsCard from '@/components/wl/settings/WlSupportSettingsCard';
import WlPartnerLogosCard from '@/components/wl/settings/WlPartnerLogosCard';
import WlDunningSettingsCard from '@/components/wl/settings/WlDunningSettingsCard';
import WlInternalTenantSettings from './WlInternalTenantSettings';
import WlCrmIntegrationSettings from './WlCrmIntegrationSettings';

type TabKey = 'geral' | 'suporte' | 'parceiros' | 'inadimplencia' | 'tenant-interno' | 'crm-integration';
const VALID_TABS: TabKey[] = ['geral', 'suporte', 'parceiros', 'inadimplencia', 'tenant-interno', 'crm-integration'];


export default function WlSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('tab') as TabKey | null;
  const active: TabKey = raw && VALID_TABS.includes(raw) ? raw : 'geral';

  const handleChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'geral') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Configurações</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Centralize as configurações do painel White Label.
        </p>
      </div>

      <Tabs value={active} onValueChange={handleChange} className="w-full">
        <TabsList>
          <TabsTrigger value="geral">Geral</TabsTrigger>
          <TabsTrigger value="suporte">Suporte</TabsTrigger>
          <TabsTrigger value="parceiros">Parceiros</TabsTrigger>
          <TabsTrigger value="inadimplencia">Inadimplência</TabsTrigger>
          <TabsTrigger value="tenant-interno">Tenant interno</TabsTrigger>
          <TabsTrigger value="crm-integration">Integração CRM</TabsTrigger>
        </TabsList>

        <TabsContent value="geral" className="mt-4">
          <WlBrandingSettingsCard />
        </TabsContent>

        <TabsContent value="suporte" className="mt-4">
          <WlSupportSettingsCard />
        </TabsContent>

        <TabsContent value="parceiros" className="mt-4">
          <WlPartnerLogosCard />
        </TabsContent>

        <TabsContent value="inadimplencia" className="mt-4">
          <WlDunningSettingsCard />
        </TabsContent>

        <TabsContent value="tenant-interno" className="mt-4">
          <WlInternalTenantSettings />
        </TabsContent>

        <TabsContent value="crm-integration" className="mt-4">
          <WlCrmIntegrationSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}

